/**
 * PlanetRenderingService - Event-driven planet rendering for multiple charts
 *
 * NEW ARCHITECTURE:
 * - Subscribes to 'chart.activated' events
 * - Makes API calls only for visible/active charts
 * - Handles lazy loading and caching
 * - Emits 'planets.rendered' events
 */

import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { jatakaApiService } from './JatakaApiService';

import { devLog, devWarn, devError } from '../utils/devLog';

interface PlanetRenderingOptions {
  forceRefresh?: boolean;
  enableAnimations?: boolean;
  debugMode?: boolean;
}

interface ChartRenderingState {
  chartId: string;
  isRendering: boolean;
  lastRendered: Date | null;
  planetCount: number;
  hasApiData: boolean;
  renderingError: string | null;
}

/**
 * Planet Rendering Service
 * Event-driven service for rendering planets in multiple charts
 */
class PlanetRenderingService {
  private renderingStates = new Map<string, ChartRenderingState>();
  private apiDataCache = new Map<string, any>();
  private subscriptions: Array<{ unsubscribe: () => void }> = [];

  constructor() {
    this.setupEventSubscriptions();
    // Debug: Initialized (production: logs disabled)
  }

  /**
   * Setup event subscriptions for chart activation
   */
  private setupEventSubscriptions(): void {
    // Subscribe to chart.loaded events for initial setup
    const chartLoadedSub = globalEventStream.subscribe('chart.loaded', (event: any) => {
      const chartId = event.chartId;
      if (!chartId) return;

      this.initializeRenderingState(chartId);
    });

    this.subscriptions.push(chartLoadedSub); // chartActivatedSub removed - planets render via TabChartRenderer
  }

  /**
   * Initialize rendering state for a chart
   */
  private initializeRenderingState(chartId: string): void {
    if (!this.renderingStates.has(chartId)) {
      this.renderingStates.set(chartId, {
        chartId,
        isRendering: false,
        lastRendered: null,
        planetCount: 0,
        hasApiData: false,
        renderingError: null
      });
      // Debug: Initialized rendering state (production: logs disabled)
    }
  }

  /**
   * Render planets for specific chart
   */
  async renderPlanetsForChart(
    chartId: string,
    options: PlanetRenderingOptions = {}
  ): Promise<boolean> {
    try {
      // Initialize state if needed
      this.initializeRenderingState(chartId);

      const state = this.renderingStates.get(chartId)!;

      // Prevent concurrent rendering
      if (state.isRendering) {
        devLog(`⏳ PlanetRenderingService: Already rendering planets for ${chartId}`);
        return false;
      }

      // Check if we need to refresh or if already rendered recently
      if (!options.forceRefresh && state.lastRendered &&
          (Date.now() - state.lastRendered.getTime()) < 5000) { // 5 second cache
        devLog(`📦 PlanetRenderingService: Using cached render for ${chartId}`);
        return true;
      }

      // Mark as rendering
      state.isRendering = true;
      state.renderingError = null;

      devLog(`🚀 PlanetRenderingService: Starting planet rendering for ${chartId}`);

      // Get chart data from MultipleChartsContext
      const multipleChartsContext = window.multipleChartsContext;
      if (!multipleChartsContext) {
        throw new Error('MultipleChartsContext not available');
      }

      const chart = multipleChartsContext.getChart(chartId);
      if (!chart) {
        throw new Error(`Chart ${chartId} not found in context`);
      }

      // Check if we already have API data
      let apiData = chart.apiResponse;
      if (!apiData || Object.keys(apiData).length === 0) {
        // Debug: Making API request (production: logs disabled)

        // Extract birth data from chart metadata for API request
        const birthData = this.extractBirthDataFromChart(chart);
        if (!birthData) {
          throw new Error('Cannot extract birth data for API request');
        }

        // Make API request
        const apiResult = await jatakaApiService.calculateJataka(birthData);
        if (!apiResult.success || !apiResult.data) {
          throw new Error(`API request failed: ${apiResult.error?.message || 'Unknown error'}`);
        }

        apiData = apiResult.data;

        // Update chart with new API data
        multipleChartsContext.updateChart(chartId, {
          apiResponse: apiData,
          isLoading: false
        });

        devLog(`✅ PlanetRenderingService: API data updated for ${chartId}`);
      }

      // Find TabChartRenderer for this chart
      const tabChartRenderers = window.tabChartRenderers;
      if (!tabChartRenderers || !tabChartRenderers.get(chartId)) {
        devWarn(`⚠️ PlanetRenderingService: No TabChartRenderer found for ${chartId}`);
        return false;
      }

      // Cast to any for runtime property access (validated below)
      const renderer = tabChartRenderers.get(chartId) as any;
      if (!renderer || typeof renderer.renderChart !== 'function') {
        devWarn(`⚠️ PlanetRenderingService: Invalid TabChartRenderer for ${chartId}`);
        return false;
      }

      // Check if planets are already rendered in TabChartRenderer
      if (renderer.renderStatus?.planets?.isRendered) {
        return true;
      }

      // Try to get existing chart data from context first (preferred)
      const existingChart = multipleChartsContext.getChart(chartId);
      let readyChartData;


      if (existingChart?.chartData && existingChart.chartData.planets && existingChart.chartData.planets.length > 0) {
        readyChartData = existingChart.chartData;
      } else {
        readyChartData = this.transformApiDataToReadyFormat(apiData, chart.metadata);
      }

      // Render with TabChartRenderer
      const renderResult = await renderer.renderChart(readyChartData, {
        displayMode: 'short',
        enableAnimations: options.enableAnimations || false,
        debugMode: options.debugMode || false,
        showMuhurtaSectors: chart.settings?.showMuhurtaChakra || false
      });

      if (renderResult.success) {
        // Update state
        state.lastRendered = new Date();
        state.planetCount = renderResult.elementsRendered;
        state.hasApiData = true;

        // Cache API data
        this.apiDataCache.set(chartId, apiData);

        // Emit planets.rendered event
        globalEventStream.emit({
          id: `planets-rendered-${chartId}-${Date.now()}`,
          type: 'planets.rendered',
          source: 'PlanetRenderingService',
          chartId,
          timestamp: Date.now(),
          metadata: {
            planetCount: renderResult.elementsRendered,
            renderTime: renderResult.renderTime,
            hasApiData: true
          }
        });

        devLog(`✅ PlanetRenderingService: Successfully rendered ${renderResult.elementsRendered} elements for ${chartId} in ${renderResult.renderTime.toFixed(2)}ms`);
        return true;
      } else {
        throw new Error('TabChartRenderer failed to render');
      }

    } catch (error) {
      const state = this.renderingStates.get(chartId);
      if (state) {
        state.renderingError = error instanceof Error ? error.message : String(error);
      }

      devError(`❌ PlanetRenderingService: Failed to render planets for ${chartId}:`, error);
      return false;

    } finally {
      // Clear rendering flag
      const state = this.renderingStates.get(chartId);
      if (state) {
        state.isRendering = false;
      }
    }
  }

  /**
   * Extract birth data from chart for API request
   */
  private extractBirthDataFromChart(chart: any): any | null {
    try {
      const metadata = chart.metadata;
      const apiResponse = chart.apiResponse;

      // Try to extract from existing API response first
      if (apiResponse?.basic_info) {
        return {
          chartName: apiResponse.basic_info.chart_name || metadata?.name || 'Chart',
          date: apiResponse.basic_info.date,
          time: apiResponse.basic_info.time,
          latitude: apiResponse.basic_info.latitude || apiResponse.basic_info.latitudeValue,
          longitude: apiResponse.basic_info.longitude || apiResponse.basic_info.longitudeValue,
          locationName: apiResponse.basic_info.location_name || metadata?.chartDateTime?.location,
          timezoneOffset: apiResponse.basic_info.timezone_offset
        };
      }

      // Fallback to metadata
      if (metadata?.chartDateTime) {
        return {
          chartName: metadata.name || 'Chart',
          date: metadata.chartDateTime.date,
          time: metadata.chartDateTime.time,
          latitude: metadata.chartDateTime.latitude,
          longitude: metadata.chartDateTime.longitude,
          locationName: metadata.chartDateTime.location,
          timezoneOffset: metadata.chartDateTime.timezoneOffset
        };
      }

      return null;
    } catch (error) {
      devError('❌ PlanetRenderingService: Failed to extract birth data:', error);
      return null;
    }
  }

  /**
   * Transform API data to ReadyChartData format
   */
  private transformApiDataToReadyFormat(apiData: any, metadata: any): any {
    // Use existing transformer utilities
    const {
      jatakaResponseToChartData
    } = require('../utils/apiDataTransformers');

    const birthData = {
      chartName: metadata?.name || apiData?.basic_info?.chart_name || 'Chart'
    };

    return jatakaResponseToChartData(apiData, birthData);
  }

  /**
   * Get rendering statistics for all charts
   */
  getStats() {
    const stats = {
      totalCharts: this.renderingStates.size,
      renderingCharts: 0,
      renderedCharts: 0,
      chartsWithErrors: 0,
      totalPlanets: 0,
      charts: {} as any
    };

    for (const [chartId, state] of this.renderingStates.entries()) {
      if (state.isRendering) stats.renderingCharts++;
      if (state.lastRendered) stats.renderedCharts++;
      if (state.renderingError) stats.chartsWithErrors++;
      stats.totalPlanets += state.planetCount;

      stats.charts[chartId] = {
        isRendering: state.isRendering,
        lastRendered: state.lastRendered,
        planetCount: state.planetCount,
        hasApiData: state.hasApiData,
        error: state.renderingError
      };
    }

    return stats;
  }

  /**
   * Cleanup service and unsubscribe from events
   */
  cleanup(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.renderingStates.clear();
    this.apiDataCache.clear();
    devLog('🧹 PlanetRenderingService: Cleaned up');
  }
}

// Create singleton instance
export const planetRenderingService = new PlanetRenderingService();

// Make it globally available for debugging
window.planetRenderingService = planetRenderingService;